import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../lib/supabase';
import { sendInverterFailureNotification } from '../lib/emailService';

const { FiX, FiSave, FiCheck, FiAlertTriangle, FiUpload, FiImage } = FiIcons;

const InverterQuickCheck = ({ inverter, onClose }) => {
  const [checkResult, setCheckResult] = useState('Bestanden');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let photoUrl = null;

      // Foto hochladen, wenn vorhanden
      if (photoFile) {
        const fileExt = photoFile.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const filePath = `inverter-checks/${fileName}`;

        // Foto in Supabase Storage hochladen
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('pv-images')
          .upload(filePath, photoFile);

        if (uploadError) {
          console.error('Error uploading photo:', uploadError);
          throw uploadError;
        }

        // Öffentliche URL für das Bild erstellen
        const { data: { publicUrl } } = supabase.storage
          .from('pv-images')
          .getPublicUrl(filePath);

        photoUrl = publicUrl;
      }

      // Aktuelles Datum und Uhrzeit
      const now = new Date().toISOString();

      // Prüfungseintrag zur Historie hinzufügen
      const { error: historyError } = await supabase
        .from('pv_inverter_history_a7b3c9d8e2')
        .insert({
          inverter_id: inverter.id,
          check_date: now,
          check_result: checkResult,
          photo_url: photoUrl,
          notes: notes.trim() || null
        });

      if (historyError) {
        console.error('Error adding history entry:', historyError);
        throw historyError;
      }

      // Aktualisiere den Haupteintrag des Wechselrichters mit dem neuesten Ergebnis
      const { error: updateError } = await supabase
        .from('pv_inverter_checks_a7b3c9d8e2')
        .update({
          check_date: now,
          check_result: checkResult,
          photo_url: photoUrl || inverter.photo_url, // Behalte das alte Foto, wenn kein neues hochgeladen wurde
          updated_at: now
        })
        .eq('id', inverter.id);

      if (updateError) {
        console.error('Error updating inverter check:', updateError);
        throw updateError;
      }

      // E-Mail-Benachrichtigung für nicht bestandene Prüfungen
      if (checkResult === 'Nicht bestanden') {
        try {
          // E-Mail-Daten speichern
          await sendInverterFailureNotification({
            inverter_name: inverter.inverter_name,
            serial_number: inverter.serial_number,
            location: inverter.location,
            notes: notes
          });

          // Zeige einen Alert an, dass die E-Mail gespeichert wurde
          setTimeout(() => {
            alert('Prüfung als "Nicht bestanden" gespeichert. Eine E-Mail-Benachrichtigung wurde für it@krieg.de vorbereitet.');
          }, 500);
        } catch (emailError) {
          console.error('Fehler beim Speichern der E-Mail-Daten:', emailError);
          alert('Prüfung gespeichert, aber E-Mail-Daten konnten nicht gespeichert werden. Bitte IT informieren.');
        }
      }

      onClose();
    } catch (error) {
      console.error('Error submitting inverter check:', error);
      alert(`Fehler beim Speichern der Prüfung: ${error.message || 'Unbekannter Fehler'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setPhotoFile(file);

    // Vorschau anzeigen
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-auto flex flex-col max-h-[90vh]"
        style={{ maxHeight: "calc(100vh - 20px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-3 border-b border-gray-200 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Wechselrichterprüfung</h2>
            <p className="text-sm text-gray-600">{inverter.inverter_name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <div className="overflow-y-auto p-3 flex-grow">
          <form className="space-y-3">
            {/* Prüfungsdaten anzeigen */}
            <div className="bg-gray-50 p-2 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <p className="text-xs text-gray-500">Seriennummer</p>
                  <p className="font-medium text-sm">{inverter.serial_number}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Standort</p>
                  <p className="font-medium text-sm">{inverter.location}</p>
                </div>
              </div>
            </div>

            {/* Prüfergebnis */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prüfergebnis
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setCheckResult('Bestanden')}
                  className={`flex items-center justify-center space-x-1 px-2 py-2 border rounded-lg ${
                    checkResult === 'Bestanden'
                      ? 'bg-green-50 border-green-300 text-green-700'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <SafeIcon
                    icon={FiCheck}
                    className={checkResult === 'Bestanden' ? 'text-green-600' : 'text-gray-400'}
                  />
                  <span className="text-sm">Bestanden</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCheckResult('Nicht bestanden')}
                  className={`flex items-center justify-center space-x-1 px-2 py-2 border rounded-lg ${
                    checkResult === 'Nicht bestanden'
                      ? 'bg-red-50 border-red-300 text-red-700'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <SafeIcon
                    icon={FiAlertTriangle}
                    className={checkResult === 'Nicht bestanden' ? 'text-red-600' : 'text-gray-400'}
                  />
                  <span className="text-sm">Nicht bestanden</span>
                </button>
              </div>
            </div>

            {/* Notizen */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notizen (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows="2"
                placeholder="Optionale Notizen zur Prüfung"
              ></textarea>
            </div>

            {/* Foto Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Foto der Prüfung (optional)
              </label>
              {photoPreview ? (
                <div className="relative border border-gray-200 rounded-lg p-1 text-center">
                  <img src={photoPreview} alt="Vorschau" className="max-h-28 mx-auto rounded-lg" />
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <SafeIcon icon={FiX} className="text-xs" />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center">
                  <SafeIcon icon={FiImage} className="text-gray-400 text-lg mx-auto mb-1" />
                  <p className="text-xs text-gray-600 mb-1">Foto hier ablegen oder auswählen</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                    id="photo-upload-quick"
                    ref={fileInputRef}
                  />
                  <label
                    htmlFor="photo-upload-quick"
                    className="bg-primary-500 text-white px-2 py-1 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer inline-flex items-center space-x-1 text-xs"
                  >
                    <SafeIcon icon={FiUpload} className="text-xs" />
                    <span>Foto auswählen</span>
                  </label>
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="flex justify-end space-x-2 p-3 border-t border-gray-200 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            Abbrechen
          </button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-primary-500 text-white px-3 py-2 rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-1 disabled:opacity-70 text-sm"
          >
            {isSubmitting ? (
              <>
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Speichern...</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiSave} className="text-sm" />
                <span>Prüfung speichern</span>
              </>
            )}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default InverterQuickCheck;